import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { MrModal, clearModal, getModalData } from '@hixme/modal'
import { connect } from 'react-redux'

// hixme-ui
import Button from '@hixme-ui/button'
import Container from '@hixme-ui/container'
import Separator from '@hixme-ui/separator'
import Text from '@hixme-ui/text'
import Input from '@hixme-ui/input'

// project
import { openCart } from 'modules/api/enrollment'
import { logGenericActivity } from 'modules/activity-log/log-generic-activity'
import { callWaiter } from 'redux-waiter'
import { getEnrollmentPublicKey } from 'store/modules/user-session/selectors'
import { actions as userSessionActions } from 'store/modules/user-session'
import { createErrorNotification } from 'modules/notification-manager/actions'

const CART_OPEN_POST = 'CART_OPEN_POST'

const UnlockCartModal = ({ enrollmentEndDateFormatted, confirm, dispatch, ...rest }) => {
  const [confirmationText, setConfirmationText] = useState('')
  const [isUnlocking, setIsUnlocking] = useState(false)

  const isConfirmButtonDisabled = confirmationText.trim().toUpperCase() !== 'YES' || isUnlocking

  const handleUnlock = () => {
    // Prevent action if confirmation text is not "YES" or if already unlocking
    if (isConfirmButtonDisabled) {
      return
    }
    setIsUnlocking(true)
    confirm(rest)
      .then(() => {
        dispatch(clearModal())
        // Reload the page after successful unlock
        window.location.reload()
      })
      .catch(() => {
        setIsUnlocking(false)
      })
  }

  const handleCancel = (e) => {
    if (e) {
      e.preventDefault()
    }
    dispatch(clearModal())
  }

  return (
    <MrModal small shouldCloseOnOverlayClick={false}>
      <Container flexColumn justifySpaceBetween alignCenter>
        <Container padding="30px 20px 0">
          <Text thin light>
            If you want to unlock your cart and continue shopping, you will have until{' '}
            <strong>{enrollmentEndDateFormatted}</strong> to shop for plans.
          </Text>

          <Text thin light p>
            If you unlock your cart, and you do not finish shopping - committing your cart again -
            by that time, you will <strong>NOT</strong> have coverage.
          </Text>

          <Text thin light p>
            Please type &quot;YES&quot; in the box below to confirm you understand, and want to
            unlock your cart.
          </Text>

          <Container margin="20px 0">
            <Input
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder='Type "YES" to confirm'
              disabled={isUnlocking}
            />
          </Container>
        </Container>

        <Container width="100%" padding="0 20px 20px 20px">
          <Separator light margin="0 0 20px 0" />
          <Container flex noPadding alignItems="center" justifyContent="space-between" width="100%">
            <Button secondary onClick={handleCancel} style={{ flex: 1, marginRight: '6px' }}>
              Cancel
            </Button>
            <Button
              onClick={handleUnlock}
              disabled={isConfirmButtonDisabled}
              submitting={isUnlocking}
              style={{
                flex: 1,
                marginLeft: '6px',
                backgroundColor: isConfirmButtonDisabled ? '#7dd87f' : '#26cd37',
                color: '#fff',
              }}
            >
              {isUnlocking ? 'Unlocking...' : 'Unlock my cart'}
            </Button>
          </Container>
        </Container>
      </Container>
    </MrModal>
  )
}

UnlockCartModal.propTypes = {
  enrollmentEndDateFormatted: PropTypes.string,
  confirm: PropTypes.func.isRequired,
}

const mapStateToProps = (state) => {
  const modalData = getModalData(state) || {}
  return {
    EnrollmentPublicKey: getEnrollmentPublicKey(state),
    enrollmentEndDateFormatted: modalData.enrollmentEndDateFormatted || '',
  }
}

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  confirm: ({ EnrollmentPublicKey }) => {
    return dispatch(
      callWaiter(CART_OPEN_POST, {
        requestCreator: () => openCart(EnrollmentPublicKey),
      })
    )
      .then(() => {
        // Log activity
        logGenericActivity({
          additionalData: {
            EnrollmentPublicKey,
          },
          activity: 'opted to unlock their cart',
          action: 'UnlockCart',
        })
        // Refresh enrollment session
        return dispatch(userSessionActions.getEnrollmentSession())
      })
      .catch((error) => {
        dispatch(createErrorNotification('Error unlocking cart'))
        throw error
      })
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(UnlockCartModal)
